import {
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material'
import { C1Card } from 'components/core/card/C1Card'
import { FieldLabel, fieldLabelString } from 'components/core/dynamic-form/fields/FieldLabel'
import { RenderFieldProps, UnknownField } from 'components/core/dynamic-form/fields/RenderField'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { C1LabeledSelectRender } from 'components/core/RHKFormComponents/C1LabeledSelect'
import { C1LabeledTextField } from 'components/core/RHKFormComponents/C1LabeledTextField'
import { SelectOneAppResourceHook } from 'components/core/search/apps'
import { SelectOneAppUserHook, SelectOneUserHook } from 'components/core/search/users'
import { AppResourceValue } from 'lib/actions/apps/app-resources'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { UserValue } from 'lib/actions/user'
import { AppResourceFilter, AppUserFilter, Field, PickerField, SelectField_SelectType, SelectOption } from 'pbts/c1/api/form/v1/form'
import { FC, ReactNode, useMemo } from 'react'
import { ControllerRenderProps } from 'react-hook-form'
import { useIntl } from 'react-intl'

export const RenderStringField: FC<RenderFieldProps> = ({ field, cField, errors, disabled }) => {
    if (field.type.oneofKind !== 'stringField') return <UnknownField field={field} />

    const stringField = field.type.stringField

    switch (stringField.view.oneofKind) {
        case 'textField':
            return <C1LabeledTextField
                label={<FieldLabel name={field.displayName} required={field.required} />}
                name={field.name}
                placeholder={stringField.placeholder || ''}
                error={!!errors[field.name]}
                helperText={(errors[field.name]?.message as string) || field.description}
                value={cField.value || ''}
                onChange={cField.onChange}
                fullWidth
                disabled={disabled}
                onBlur={cField.onBlur}
                multiline={stringField.view.textField.multiline}
                maxRows={stringField.view.textField.multiline ? 8 : undefined}
                minRows={stringField.view.textField.multiline ? 4 : undefined}
            />
        case 'passwordField':
            return <C1LabeledTextField
                label={<FieldLabel name={field.displayName} required={field.required} />}
                name={field.name}
                placeholder={stringField.placeholder || ''}
                error={!!errors[field.name]}
                helperText={(errors[field.name]?.message as string) || field.description}
                value={cField.value || ''}
                onBlur={cField.onBlur}
                onChange={cField.onChange}
                type="password"
                disabled={disabled}
                fullWidth
            />
        case 'selectField':
            return <RenderSelectFieldView
                field={field}
                cField={cField}
                errors={errors}
                disabled={disabled}
                selectField={stringField.view.selectField}
                placeholder={stringField.placeholder}
            />
        case 'pickerField':
            return <RenderPickerFieldView
                field={field}
                cField={cField}
                errors={errors}
                disabled={disabled}
                pickerField={stringField.view.pickerField}
            />
        default:
            return <UnknownField field={field} />
    }
}

type RenderSelectFieldViewProps = {
    field: RenderFieldProps['field']
    cField: RenderFieldProps['cField']
    errors: RenderFieldProps['errors']
    disabled: boolean
    placeholder?: string
    selectField: {
        type: SelectField_SelectType
        options: SelectOption[]
    }
}

const RenderSelectFieldView: FC<RenderSelectFieldViewProps> = ({
    field,
    cField,
    errors,
    disabled,
    placeholder,
    selectField,
}) => {
    const { type, options } = selectField

    switch (type) {
        case SelectField_SelectType.RADIO:
            return (
                <FormControl error={!!errors[field.name]} disabled={disabled} fullWidth>
                    <C1FormLabel label={<FieldLabel name={field.displayName} required={field.required} />} />
                    {field.description && (
                        <Typography variant="caption" color="text.secondary" mb={1} display="block">
                            {field.description}
                        </Typography>
                    )}
                    <RadioGroup
                        value={cField.value || ''}
                        onChange={(e): void => cField.onChange(e.target.value)}
                        onBlur={cField.onBlur}
                    >
                        {options.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio />}
                                label={
                                    <Typography variant="body1">{option.displayName}</Typography>
                                }
                                disabled={disabled}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            )

        case SelectField_SelectType.BUTTONS:
            return (
                <FormControl error={!!errors[field.name]} disabled={disabled} fullWidth>
                    <C1FormLabel label={<FieldLabel name={field.displayName} required={field.required} />} />
                    {field.description && (
                        <Typography variant="caption" color="text.secondary" mb={1} display="block">
                            {field.description}
                        </Typography>
                    )}
                    <Stack spacing={2}>
                        {options.map((option) => (
                            <C1Card
                                key={option.value}
                                onClick={disabled ? undefined : (): void => cField.onChange(option.value)}
                                isSelected={cField.value === option.value}
                                sx={{
                                    p: 2,
                                    mb: 0,
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: disabled ? 0.5 : 1,
                                }}
                                ariaLabel={option.displayName}
                            >
                                <Stack spacing={0.5}>
                                    <Typography variant="body1">
                                        {option.displayName}
                                    </Typography>
                                    {option.description && (
                                        <Typography variant="caption" color="text.secondary">
                                            {option.description}
                                        </Typography>
                                    )}
                                </Stack>
                            </C1Card>
                        ))}
                    </Stack>
                </FormControl>
            )

        // DROPDOWN and UNSPECIFIED both use the dropdown
        case SelectField_SelectType.DROPDOWN:
        case SelectField_SelectType.UNSPECIFIED:
        default: {
            const dropdownOptions = options.map((o) => ({
                id: o.value,
                value: o.value,
                label: o.displayName,
            }))

            return <C1LabeledSelectRender
                name={field.name}
                value={cField.value ?? undefined}
                onChange={(e): void => cField.onChange(e.target.value)}
                onBlur={cField.onBlur}
                label={<FieldLabel name={field.displayName} required={field.required} />}
                options={dropdownOptions}
                error={!!errors?.[field.name]}
                helperText={(errors?.[field.name]?.message as string) || field.description}
                renderValue={(value): ReactNode => (value as string) || <Typography
                    component="span"
                    color="text.disabled"
                >
                    {placeholder || ''}
                </Typography>}
                disabled={disabled}
                fullWidth
                displayEmpty
            />
        }
    }
}

type RenderPickerFieldViewProps = {
    field: RenderFieldProps['field']
    cField: RenderFieldProps['cField']
    errors: RenderFieldProps['errors']
    disabled: boolean
    pickerField: PickerField
}

const RenderPickerFieldView: FC<RenderPickerFieldViewProps> = ({ field, cField, errors, disabled, pickerField }) => {
    switch (pickerField.type.oneofKind) {
        case 'appUserPicker':
            return <RenderAppUserPicker
                field={field}
                cField={cField}
                errors={errors}
                disabled={disabled}
                appUserPicker={pickerField.type.appUserPicker}
            />
        case 'resourcePicker':
            return <RenderResourcePicker
                field={field}
                cField={cField}
                errors={errors}
                disabled={disabled}
                resourcePicker={pickerField.type.resourcePicker}
            />
        case 'c1UserPicker':
            return <RenderC1UserPicker
                field={field}
                cField={cField}
                errors={errors}
                disabled={disabled}
            />
        default:
            return <UnknownField field={field} />
    }
}

type RenderAppUserPickerProps = {
    field: Field
    cField: ControllerRenderProps
    errors: RenderFieldProps['errors']
    disabled: boolean
    appUserPicker: AppUserFilter
}

const RenderAppUserPicker: FC<RenderAppUserPickerProps> = ({ field, cField, errors, disabled, appUserPicker }) => {
    const intl = useIntl()
    const { appId } = appUserPicker

    const initRef = useMemo(() => {
        if (!cField.value) return undefined
        return { id: cField.value as string, appId }
    }, [cField.value, appId])

    return <SelectOneAppUserHook
        onSelected={(appUser: AppUserValue | null): void => cField.onChange(appUser?.id || '')}
        initAppUserRef={initRef}
        reqOptions={{ appId }}
        disabled={disabled}
        onBlur={cField.onBlur}
        label={fieldLabelString(field.displayName, field.required, intl)}
        helperText={(errors[field.name]?.message as string) || field.description}
    />
}

type RenderResourcePickerProps = {
    field: Field
    cField: ControllerRenderProps
    errors: RenderFieldProps['errors']
    disabled: boolean
    resourcePicker: AppResourceFilter
}

const RenderResourcePicker: FC<RenderResourcePickerProps> = ({ field, cField, errors, disabled, resourcePicker }) => {
    const intl = useIntl()
    const { appId, resourceTypeId } = resourcePicker

    const initRef = useMemo(() => {
        if (!cField.value) return undefined
        return {
            id: cField.value as string,
            appId,
            appResourceTypeId: resourceTypeId,
        }
    }, [cField.value, appId, resourceTypeId])

    const baseRequest = useMemo(() => ({
        appId: appId || undefined,
        resourceTypeIds: resourceTypeId ? [resourceTypeId] : undefined,
    }), [appId, resourceTypeId])

    return <SelectOneAppResourceHook
        onSelected={(resource: AppResourceValue | null): void => cField.onChange(resource?.id || '')}
        initAppResourceRef={initRef}
        baseRequest={baseRequest}
        disabled={disabled}
        onBlur={cField.onBlur}
        label={fieldLabelString(field.displayName, field.required, intl)}
        helperText={(errors[field.name]?.message as string) || field.description}
    />
}

type RenderC1UserPickerProps = {
    field: Field
    cField: ControllerRenderProps
    errors: RenderFieldProps['errors']
    disabled: boolean
}

const RenderC1UserPicker: FC<RenderC1UserPickerProps> = ({ field, cField, errors, disabled }) => {
    const intl = useIntl()

    return <SelectOneUserHook
        onSelected={(user: UserValue | null): void => cField.onChange(user?.id || '')}
        initUserId={cField.value as string || undefined}
        disabled={disabled}
        onBlur={cField.onBlur}
        label={fieldLabelString(field.displayName, field.required, intl)}
        helperText={(errors[field.name]?.message as string) || field.description}
    />
}
